import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useWorkspaces } from '../hooks/useClaw9';
import CreateWorkspaceModal from './CreateWorkspaceModal';
import WorkspaceDetailsModal from './WorkspaceDetailsModal';

/**
 * Panel displaying list of workspaces with actions
 */
function WorkspaceListPanel({ className = '' }) {
    const { workspaces, isLoading, error, deleteWorkspace, refresh } = useWorkspaces();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async (workspaceId) => {
        setIsDeleting(true);
        try {
            await deleteWorkspace(workspaceId);
            setDeleteConfirmId(null);
        } catch {
            // Error handled by hook
        } finally {
            setIsDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    if (isLoading && workspaces.length === 0) {
        return (
            <div className={`flex items-center justify-center h-64 ${className}`}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
        );
    }

    return (
        <div className={className}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-medium text-gray-900">Workspaces</h2>
                    <p className="text-sm text-gray-500">
                        {workspaces.length} workspace{workspaces.length !== 1 ? 's' : ''}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={refresh}
                        disabled={isLoading}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                    >
                        Create Workspace
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            {/* Workspace List */}
            {workspaces.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No workspaces</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by creating a new workspace.</p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="mt-4 px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                    >
                        Create Workspace
                    </button>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Bot ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Files
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Size
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Modified
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {workspaces.map(workspace => (
                                <tr key={workspace.bot_id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                                <svg className="h-5 w-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                                </svg>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {workspace.bot_id}
                                                </div>
                                                <div className="text-sm text-gray-500 truncate max-w-xs" title={workspace.path}>
                                                    {workspace.path}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                            {workspace.file_count || 0} files
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatBytes(workspace.size_bytes)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(workspace.modified_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => setSelectedWorkspace(workspace)}
                                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                                        >
                                            View
                                        </button>
                                        {deleteConfirmId === workspace.bot_id ? (
                                            <span className="inline-flex gap-2">
                                                <button
                                                    onClick={() => handleDelete(workspace.bot_id)}
                                                    disabled={isDeleting}
                                                    className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                                >
                                                    {isDeleting ? 'Deleting...' : 'Confirm'}
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirmId(null)}
                                                    className="text-gray-600 hover:text-gray-900"
                                                >
                                                    Cancel
                                                </button>
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => setDeleteConfirmId(workspace.bot_id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <CreateWorkspaceModal
                    onClose={() => setShowCreateModal(false)}
                    onCreated={() => {
                        setShowCreateModal(false);
                        refresh();
                    }}
                />
            )}

            {/* Details Modal */}
            {selectedWorkspace && (
                <WorkspaceDetailsModal
                    workspace={selectedWorkspace}
                    onClose={() => setSelectedWorkspace(null)}
                />
            )}
        </div>
    );
}

WorkspaceListPanel.propTypes = {
    className: PropTypes.string
};

export default WorkspaceListPanel;
